import { PROJECT_LOGOS } from "@/assets/static/images";
import Project from "@/types/Project";

export const sourena: Project = {
    logo: PROJECT_LOGOS.sourena,
    colors: ["#F0EAE0", "#4F5277"],
    name: "Sourena Perfumery Platform",
    description:
        "A high-performance full-stack eCommerce ecosystem engineered for a luxury perfumery. Developed with Next.js (App Router) on the frontend and Spring Boot (Kotlin) on the backend, it features a distributed architecture supporting multi-region localization, real-time dynamic currency conversion, an advanced custom rich-text parser, and a secure role-based access control system.",
    skills: ["Frontend Architecture", "Backend Engineering", "DevOps & Data Layer"],
    tech_stack_ids: [
        "nextjs",
        "react",
        "typescript",
        "scss",
        "spring-boot",
        "kotlin",
        "postgresql",
        "redis",
        "rest-api",
        "websocket",
        "i18n",
        "prisma",
        "cicd",
        "docker",
    ],
    company_id: "sourena",
    started_date: "Nov 24’",
    ended_date: "Nov 25’",
    highlights: [
        "Dynamic multi-currency engine integrating automated exchange rate updates for real-time localized pricing",
        "Custom rich-text parser and compiler supporting dynamic typography, inline icons, and embedded media layers",
        "Granular role-based access control (RBAC) permitting secure administrative operations across multiple user clearance levels",
        "Multi-tier caching architecture leveraging Redis for session/catalog data and Next.js ISR (Incremental Static Regeneration)",
        "Automated CI/CD pipeline integrated with Jest and JUnit testing frameworks, ensuring zero-regression continuous deployment",
        "Custom design system built from the ground up utilizing structured SCSS mixins for consistent, responsive visual rendering",
    ],
    case_study: {
        slug: "sourena-perfumery-platform",
        project_name: "Sourena Perfumery Platform",
        preview_video_src: "/case_studies/sourena/scenes/scene_4",
        thumbnail_image: PROJECT_LOGOS.sourena,
        hero_image: "/case_studies/sourena/hero_image.webp",
        hero_title: "Building an eCommerce Platform for a Perfume Brand",
        hero_subtitle:
            "A full-stack eCommerce platform with advance features & approaches focus on foundation architecture",
        overview: {
            role: "Full-Stack Developer",
            team_size: "Solo Developer",
            platforms: ["Website"],
            summary:
                "<strong>The Problem:</strong> A growing perfume brand needed a scalable eCommerce platform to expand globally, but struggled with rigid catalog systems and high developer dependencies. <strong>The Solution:</strong> I built a full-stack eCommerce ecosystem from scratch using Next.js, Kotlin/Spring Boot, and PostgreSQL, featuring dynamic multi-currency rates, an intuitive custom rich-text editor, and secure RBAC. <strong>The Result:</strong> The platform achieved 99.9% uptime, reduced manual updates by 40%, and enabled seamless multi-region operations.",
        },
        tech_details: {
            title: "Technical Excellence",
            description:
                "A deep dive into the engineering decisions that ensure the platform remains resilient under load and maintainable for future expansion.",
            architecture_highlights: [
                "<strong>Distributed Backend:</strong> Kotlin (Spring Boot) providing type-safety and superior multi-threading via Coroutines.",
                "<strong>Modern Frontend:</strong> Next.js 16 with Turbopack for sub-second hot-reloads and optimized production builds.",
                "<strong>Type-Safe Contracts:</strong> Shared TypeScript interfaces and strict API validation using Zod/JSR-303.",
                "<strong>Performance Layer:</strong> Multi-tier caching with Redis (session/catalog) and Next.js Data Cache.",
                "<strong>Real-time Infrastructure:</strong> WebSocket-based admin notifications for order management and catalog sync.",
            ],
        },
        gallery: [
            {
                type: "video",
                src: "/case_studies/sourena/scenes/scene_4",
                caption:
                    'As i already mentioned i\'m an android developer too, so made something like <a href="https://developer.android.com/develop/ui/compose/animation/shared-elements" target="_blank" rel="noopener noreferrer">Shared element transitions in Compose</a> that makes the navigation between pages super fluent & smooth. I made this before next js make <a href="https://nextjs.org/docs/app/api-reference/config/next-config-js/viewTransition" target="_blank" rel="noopener noreferrer">View Transition</a> witch it\'s still an experimental feature in next js.',
                muted: true,
                autoplay: true,
                loop: true,
            },
            {
                type: "video",
                src: "/case_studies/sourena/scenes/scene_1",
                caption: "Showcasing marketplace phase of the website with multi-currency & multi-language support.",
                muted: true,
                autoplay: true,
                loop: true,
            },
            {
                type: "video",
                src: "/case_studies/sourena/scenes/scene_2",
                caption:
                    "Showcasing <b>Special Text editor</b> inside article crud of administrative area. actually, this special text editor that appears as a modal has used for content creation and it will apply the format syntax regex by highlighting and clicking on any specific format token like bold, italic, highlight, shimmer, and etc.",
                muted: true,
                autoplay: true,
                loop: true,
            },
            {
                type: "video",
                src: "/case_studies/sourena/scenes/scene_3",
                caption:
                    "Showcasing product page witch can have video for its hero section, and also, as you can see, the brand tape is moving while scrolling, and the special text format also used in here belong the criterias (in data layers, I defined product type and each product type has some criteria with their unit, range, and visibility in chart witch makes each product to have a type and values for the criteria types. more information written more below). Also, i made an optional feature for product that took the raw transparent object picture of the product and represent it in a showcase with animated bobbles and headline witch is dynamic and moves though the z layers of the showcase stack.",
                muted: true,
                autoplay: true,
                loop: true,
            },
            {
                type: "video",
                src: "/case_studies/sourena/scenes/scene_5",
                caption:
                    'Another phase of the website called <a href="https://en.wikipedia.org/wiki/Aromatherapy" target="_blank" rel="noopener noreferrer">Aromatherapy</a>. On the aromatherapy page, i provided a list of meditation sessions (crudable in administrative area from the locales to the audio, cover, texts, and etc.) witch each session has their own audio that allows customers to come, set a timer, and play a session witch at first, they\'ll do breathing exercise that has a super cool animated visuals and after that, the audio will be play and also, I also made some particles around the page and animated countdown to make more harmony.',
                muted: true,
                autoplay: true,
                loop: true,
            },
            {
                type: "video",
                src: "/case_studies/sourena/scenes/scene_6",
                caption: `Showcasing the metal animated company logo in <a href="https://sourena.store/en/about_us" target="_blank" rel="noopener noreferrer">About us</a> page of the website.`,
                muted: true,
                autoplay: true,
                loop: true,
            },
        ],
        architecture_and_scale: {
            title: "Architecture & Trade-offs",
            description:
                "Engineering is about choosing the right trade-offs. Here is why I chose specific technologies over common alternatives.",
            trade_offs: [
                {
                    decision: "Kotlin/Spring Boot vs. Node.js/Express",
                    rationale:
                        "While Node.js is excellent for I/O bound tasks, I chose Kotlin for the backend to leverage its strict type system and superior handling of complex business logic. Spring Boot's robust ecosystem for security and database transactions reduced the risk of 'callback hell' and state inconsistencies in the multi-currency checkout flow.",
                },
                {
                    decision: "SCSS Modules vs. Tailwind CSS",
                    rationale:
                        "I opted for SCSS Modules to maintain a clean separation between logic and styling. This allowed for a highly customized design system using mixins and variables that would have been overly verbose and harder to maintain in a utility-first framework like Tailwind.",
                },
                {
                    decision: "SQL (PostgreSQL) vs. NoSQL (MongoDB)",
                    rationale:
                        "Given the transactional nature of eCommerce (orders, inventory, payments), PostgreSQL was the clear choice for its ACID compliance and relational integrity, ensuring that inventory counts never drift during high-concurrency checkout events.",
                },
            ],
        },
        infrastructure: {
            title: "Deployment & CI/CD",
            description:
                "Automation is the foundation of reliability. The platform is engineered for zero-downtime deployments.",
            details: [
                "<strong>Frontend Deployment:</strong> Hosted on Vercel for global Edge distribution and optimized SSR performance.",
                "<strong>Backend Infrastructure:</strong> Containerized with Docker and deployed to a scalable cloud environment.",
                "<strong>CI/CD Pipeline:</strong> Automated GitHub Actions workflow for linting, type-checking, and unit testing before every deployment.",
                "<strong>Database Management:</strong> Managed PostgreSQL with automated daily backups and point-in-time recovery.",
                "<strong>Security Headers:</strong> Strict Content Security Policy (CSP), HSTS, and X-Frame-Options enforced at the edge.",
            ],
        },
        results: [
            {
                metric: "Operational Efficiency",
                value: "40% Gain",
                description:
                    "Automated catalog management and localized pricing reduced manual content updates by 40%, allowing the team to focus on marketing rather than data entry.",
                colors: ["#FFB347", "#FF7A7A", "#D65BFF"],
            },
            {
                metric: "Market Readiness",
                value: "Global",
                description:
                    "Shipped 5-language support and multi-currency checkout, positioning the brand for immediate international expansion and multi-region revenue streams.",
                colors: ["#7FD7FF", "#5FA8FF", "#7A7CFF"],
            },
            {
                metric: "System Reliability",
                value: "99.9% Uptime",
                description:
                    "Leveraged Spring Boot's robust transactional model and Vercel's edge network to maintain high availability during peak seasonal traffic.",
                colors: ["#5ED3B3", "#4FA9D8", "#6B8CF5"],
            },
            {
                metric: "Release Velocity",
                value: "2x Faster",
                description:
                    "Standardized component design and CI/CD automation doubled the speed of feature delivery from prototype to production deployment.",
                colors: ["#2BD9A4", "#1FBF89", "#2A9D8F"],
            },
            {
                metric: "Conversion Integrity",
                value: "Secure",
                description:
                    "Implemented strict security headers and atomic database transactions to ensure 100% integrity for sensitive customer data and payment flows.",
                colors: ["#FFD6A5", "#FFADAD", "#E598FF"],
                foregroundColor: "#FFADAD",
            },
            {
                metric: "Technical Debt",
                value: "Minimized",
                description:
                    "Maintained a high-quality codebase through strict type-safety and Clean Architecture, reducing long-term maintenance costs and developer onboarding time.",
                colors: ["#C3F584", "#7BE495", "#4DD599"],
                foregroundColor: "#7BE495",
            },
        ],
        challenge: {
            title: "The Challenge",
            description:
                "Building an eCommerce platform that’s easy to manage, scales globally, and stays maintainable as the catalog and content evolve. Below this page, i've explained how i managed the challenges and made solutions for them.",
            points: [
                "<strong>Reduce engineering dependency:</strong> Make routine content/catalog updates self-serve for non-technical users.",
                "<strong>Internationalization without duplication:</strong> Keep language support scalable without cloning UI logic or creating SEO regressions.",
                "<strong>Predictable catalog rules:</strong> Model variants/pricing clearly so catalog changes don’t ripple into edge-case bugs.",
                "<strong>Performance budgets:</strong> Keep browsing responsive as media and inventory grow.",
                "<strong>Checkout that’s easy to debug:</strong> Keep the flow consistent so issues are diagnosable and fixes are low-risk.",
                "<strong>Lower release risk:</strong> Prefer small changes with automated checks so updates remain reviewable and reversible.",
                "<strong>Baseline security:</strong> Validation, sensible permissions, and strict browser policies where applicable.",
            ],
        },
        solution: {
            title: "Key Decisions",
            description:
                "I optimized for three outcomes: <em>maintainability</em>, <em>speed</em>, and <em>clarity</em>. The guiding rule was simple: keep the system predictable and only add complexity when it reduced long-term risk or operational cost.",
            approaches: [
                {
                    title: "Frontend decisions (clarity first)",
                    description:
                        "Used <strong>Next.js</strong> and <strong>React</strong> with patterns chosen to avoid hidden coupling and reduce UI regressions.",
                    techniques: [
                        "Kept routing/data loading predictable to reduce edge cases and debugging time",
                        "Preferred local, explicit state and fixed re-render hotspots in key flows",
                        "Standardized component patterns so new screens match existing behavior",
                        "Used motion only when it communicated state change; avoided decorative animation debt",
                        "Defined responsive rules early to prevent breakpoint regressions",
                        "Built structured editing so published content stays consistent and reviewable",
                    ],
                },
                {
                    title: "Backend decisions (predictable APIs)",
                    description:
                        "Backend built with <strong>Kotlin</strong> (Spring Boot) with an emphasis on clear contracts, validation, and business rules that are easy to change safely.",
                    techniques: [
                        "Defined API contracts with validation and consistent errors to reduce ambiguous edge cases",
                        "Implemented role-based access where needed to keep staff workflows safe",
                        "Added caching (<strong>Redis</strong>) on read-heavy paths to keep browsing responsive",
                        "Used real-time only where it reduced confusion (e.g., admin feedback loops)",
                        "Added CI checks and targeted tests around checkout and CMS-critical flows",
                    ],
                },
                {
                    title: "Internationalization (scales without copy-paste)",
                    description:
                        "Implemented internationalization in a way that stays maintainable as languages and content expand.",
                    techniques: [
                        "Centralized translations to avoid drift and duplicated copy",
                        "Kept localized routes/metadata consistent to preserve SEO fundamentals",
                        "Made translations part of the publishing workflow so updates remain practical",
                    ],
                },
                {
                    title: "Data model (reduce surprise bugs)",
                    description:
                        "Used <strong>PostgreSQL</strong> with explicit catalog rules to keep pricing/variants predictable and reduce data-driven edge cases.",
                    techniques: [
                        "Modeled products/variants/orders with clear constraints so rules are enforced in one place",
                        "Added indexes based on real query paths to keep key pages responsive",
                        "Used migrations/versioning to keep schema changes safe and repeatable",
                        "Prioritized data integrity to prevent silent corruption and hard-to-reproduce bugs",
                    ],
                },
                {
                    title: "Delivery (lower release risk)",
                    description: "Set up a build and release workflow that supports safe iteration.",
                    techniques: [
                        "Made lint/typecheck/build part of CI to reduce broken deploys",
                        "Containerized where it reduced environment differences",
                        "Added practical logging/monitoring hooks for faster diagnosis",
                        "Shipped in small steps so changes were reviewable and rollbacks were straightforward",
                    ],
                },
            ],
        },
        conclusion: {
            title: "Conclusion & Reflection",
            content:
                "This project reinforced a simple lesson: the best results come from balancing product needs with engineering discipline. The goal wasn’t to be flashy—it was to build a dependable platform the business can run daily, with a user experience that feels clear and intentional.",
            key_takeaways: [
                "<strong>Make content self-serve:</strong> Good tooling reduces back-and-forth and keeps teams moving.",
                "<strong>Keep the model predictable:</strong> Clear rules for variants/pricing prevent fragile edge cases.",
                "<strong>Use performance budgets:</strong> Treat animations and media as a cost—add them only when they help.",
                "<strong>Ship in small steps:</strong> Smaller releases are easier to review, test, and roll back.",
                "<strong>Write for future you:</strong> Naming, structure, and constraints matter as the platform grows.",
            ],
        },
    },
};
